import { type AssignedTask, TaskV2 } from '@/models/registry';
import { makeAutoObservable } from 'mobx';

type CopilotState = {
  isActive: boolean;
  taskIds: string[];
  currentIndex: number;
  taskReasons: Record<string, string>;
};

class MyTasksCopilotStore {
  state: CopilotState = {
    isActive: false,
    taskIds: [],
    currentIndex: 0,
    taskReasons: {},
  };

  constructor() {
    makeAutoObservable(this);
  }

  startCopilot(
    tasks: AssignedTask[] | TaskV2[],
    taskReasons: Record<string, string> = {},
  ) {
    this.state = {
      isActive: true,
      taskIds: tasks.map((task) => task.id),
      currentIndex: 0,
      taskReasons,
    };
  }

  stopCopilot() {
    this.state = {
      isActive: false,
      taskIds: [],
      currentIndex: 0,
      taskReasons: {},
    };
  }

  nextTask(): boolean {
    if (this.state.currentIndex < this.state.taskIds.length - 1) {
      this.state.currentIndex++;
      return true;
    }
    return false;
  }

  prevTask(): boolean {
    if (this.state.currentIndex > 0) {
      this.state.currentIndex--;
      return true;
    }
    return false;
  }

  get currentTaskId(): string | null {
    if (!this.state.isActive || this.state.taskIds.length === 0) {
      return null;
    }
    return this.state.taskIds[this.state.currentIndex] || null;
  }

  get currentTaskReason(): string | null {
    const taskId = this.currentTaskId;
    if (!taskId) return null;
    return this.state.taskReasons[taskId] || null;
  }

  get hasNext(): boolean {
    return this.state.currentIndex < this.state.taskIds.length - 1;
  }

  get hasPrev(): boolean {
    return this.state.currentIndex > 0;
  }

  get isActive(): boolean {
    return this.state.isActive;
  }

  get currentPosition(): { current: number; total: number } {
    return {
      current: this.state.currentIndex + 1,
      total: this.state.taskIds.length,
    };
  }
}

export const myTasksCopilotStore = new MyTasksCopilotStore();
